import {addDays, addHours, addMinutes, format} from "date-fns";
import {TFunction} from "i18next";
import EventSummaryItem from "../stores/domainStore/pages/models/eventSummaryItem";
import {utcToZonedTime} from "date-fns-tz";
import {parseDuration, getDuration} from "../utils/dateUtils";

export class eventCsvExporterConfigs {
  static fileExtension: string = ".csv";
  static wrapColumnChar: string = '"';
  static requiresBookingKey: string = "requiresBooking";
  static price: string = "price";
  static startTime: string = "startTime";

  /**
   * Get column names for resident admin list
   * @param t translation function
   * @param isEnhanced to differentiate the type of calendar. Enhanced or Engage.
   * @param isPayByFeeEnabled to indifcate if the feature, pay by fee is enabled. Default to false
   * @returns a csv column name array
   */
  static getColumns(
    t: TFunction,
    isEnhanced: boolean,
    isPayByFeeEnabled: boolean = false
  ): any[] {
    const columns = [
      {
        id: "title",
        displayName: t("event:labels.title")
      },
      {
        id: "category",
        displayName: t("event:labels.category")
      },
      {
        id: "startDate",
        displayName: t("event:labels.startDate")
      },
      {
        id: "startTime",
        displayName: t("event:labels.startTime")
      },
      {
        id: "isFeaturedEvent",
        displayName: isEnhanced
          ? t("event:labels.isFeatured")
          : t("event:labels.special")
      },
      {
        id: "occurs",
        displayName: t("event:labels.recurrenceOccurs")
      },
      {
        id: "location",
        displayName: t("event:csv.columnLocation")
      },
      {
        id: eventCsvExporterConfigs.price,
        displayName: t("event:labels.price.pricing")
      },
      {
        id: eventCsvExporterConfigs.requiresBookingKey,
        displayName: t("event:labels:booking.bookingRequirement")
      },
      {
        id: "status",
        displayName: t("event:labels.eventTemplateStatus")
      }
    ];

    if (isEnhanced) {
      const startTimeIndex = columns.findIndex(
        (x) => x.id === eventCsvExporterConfigs.startTime
      );
      columns.splice(
        startTimeIndex + 1,
        0,
        {
          id: "endDate",
          displayName: t("event:labels.endDate")
        },
        {
          id: "endTime",
          displayName: t("event:labels.endTime")
        },
        {
          id: "duration",
          displayName: t("event:labels.duration")
        }
      );

      const requiresBookingIndex = columns.findIndex(
        (x) => x.id === eventCsvExporterConfigs.requiresBookingKey
      );
      columns.splice(
        requiresBookingIndex + 1,
        0,
        {
          id: "totalSpaces",
          displayName: t("event:labels.space.totalSpace")
        },
        {
          id: "staffAssisted",
          displayName: t("event:labels.staffAssisted")
        },
        {
          id: "remainingSpaces",
          displayName: t("event:labels.space.remainingSpace")
        },
        {
          id: "totalConfirmedEventBookings",
          displayName: t("event:labels.bookingStatus.confirmed")
        },
        {
          id: "totalWaitlistedBookings",
          displayName: t("event:labels.bookingStatus.waitlist")
        },
        {
          id: "totalCancelledBookings",
          displayName: t("event:labels.bookingStatus.cancelled")
        },
        {
          id: "deadline",
          displayName: t("event:labels.booking.deadline")
        },
        {
          id: "description",
          displayName: t("event:labels.description")
        }
      );

      if (isPayByFeeEnabled) {
        // To put the cost allocation column after the price column
        const priceIndex = columns.findIndex(
          (x) => x.id === eventCsvExporterConfigs.price
        );
        columns.splice(priceIndex + 1, 0, {
          id: "costAllocation",
          displayName: t("event:labels.price.costAllocation")
        });
      }

      columns.push({
        id: "message",
        displayName: t("event:labels.reason")
      });
    }

    return columns;
  }

  /**
   * Map event to export row and replace all double quotes with two double quotes for CSV format
   */
  static mapEventToExportRow(
    event: EventSummaryItem,
    t: TFunction,
    isEnhanced: boolean,
    isPayByFeeEnabled: boolean = false
  ) {
    const row = {
      title: escapeWrapCharacter(event.title),
      category: escapeWrapCharacter(event.category),
      startDate: event.occurrenceDate
        ? format(new Date(event.occurrenceDate), "dd/MM/yyyy")
        : format(new Date(event.start), "dd/MM/yyyy"), // If occurrence date is not available, use start date (in case of OneOff events)
      startTime: format(new Date(event.start), "HH:mm"),
      isFeaturedEvent: event.extendedProps.isFeaturedEvent
        ? t("event:labels.yes")
        : t("event:labels.no"),
      occurs: event.extendedProps.eventRecurrenceDetails.frequency,
      location: escapeWrapCharacter(event.location),
      price:
        event.price?.amount == null
          ? t("event:labels.price.ownExpense")
          : event.price.amount > 0
            ? "$" + event.price.amount
            : t("event:labels.price.free"),
      requiresBooking: event.extendedProps.isBookableEvent
        ? t("event:labels.yes")
        : t("event:labels.no"),
      status: event.extendedProps.eventStatus,
      message: escapeWrapCharacter(event.extendedProps.statusChangeReason)
    } as any;

    if (isEnhanced) {
      row.endDate = format(new Date(event.end), "dd/MM/yyyy");
      row.endTime = format(new Date(event.end), "HH:mm");
      row.duration = getDuration(event.start, event.end);
      row.totalSpaces = CheckIfEventIsUnlimited(
        event.extendedProps.isBookableEvent,
        event.extendedProps.capacity
      )
        ? t("event:csv.unlimited")
        : IfNotNull(event.extendedProps.capacity);

      row.remainingSpaces = CheckIfEventIsUnlimited(
        event.extendedProps.isBookableEvent,
        event.extendedProps.capacity
      )
        ? t("event:csv.unlimited")
        : IfNotNull(event.extendedProps.capacityRemaining);
      row.staffAssisted = IfNotNull(event.extendedProps.reservedCapacity);
      row.totalConfirmedEventBookings = IfNotNull(
        event.extendedProps.totalBookings
      );
      row.totalWaitlistedBookings = IfNotNull(
        event.extendedProps.totalWaitlistedBookings
      );
      row.totalCancelledBookings = IfNotNull(
        event.extendedProps.totalCancelledBookings
      );
      row.deadline = deadlineClosedDate(
        event.start,
        event.timeZone,
        event.extendedProps.bookingDeadline
      );
      row.description = escapeWrapCharacter(event.description);
    }

    if (isPayByFeeEnabled) {
      row.costAllocation = event.price?.costAllocation
        ? t(`event:labels.costAllocation.${event.price?.costAllocation}`)
        : "";
    }
    return row;

    function CheckIfEventIsUnlimited(
      isBookableEvent: boolean,
      capacity: number | null | undefined
    ) {
      return isBookableEvent && capacity === null;
    }
    function IfNotNull(prop: number | undefined | null) {
      return prop === null || prop === undefined ? "" : prop.toString();
    }

    function deadlineClosedDate(
      startDate: Date,
      timeZone: string,
      deadline: string | null | undefined
    ) {
      if (!deadline) {
        return "";
      }

      const {weeks, days, hours, minutes} = parseDuration(deadline);

      const timezonedStartDate = utcToZonedTime(startDate, timeZone);
      const date = addMinutes(
        addHours(addDays(timezonedStartDate, -(weeks * 7 + days)), -hours),
        -minutes
      );

      return format(date, "dd/MM/yyyy HH:mm");
    }

    function escapeWrapCharacter(text: string | undefined) {
      if (!text) return;

      return text
        .split(eventCsvExporterConfigs.wrapColumnChar)
        .join(eventCsvExporterConfigs.wrapColumnChar.repeat(2));
    }
  }
}
